import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { UserProfile } from '../types/UserProfile';

const BOOTSTRAP_CACHE_KEY = 'verbo_bootstrap_cache_v2';
const USER_PROFILE_TTL = 1000 * 60 * 60; // 1 hour
const LAST_SEEN_TTL = 1000 * 60 * 60 * 6; // 6 hours

interface BootstrapCache {
  userProfile: UserProfile | null;
  appConfig: any | null;
  timestamp: number;
  lastSeenUpdate: number;
  uid: string | null;
}

class BootstrapService {
  private inMemoryCache: BootstrapCache | null = null;

  private getLocalCache(): BootstrapCache | null {
    const raw = localStorage.getItem(BOOTSTRAP_CACHE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private setLocalCache(cache: BootstrapCache) {
    try {
      localStorage.setItem(BOOTSTRAP_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.warn('[bootstrap] Failed to set local cache', e);
    }
  }

  async bootstrap(uid: string): Promise<{ user: UserProfile | null; config: any | null }> {
    const now = Date.now();
    const local = this.getLocalCache();

    // 1. Use in-memory if available and same user
    if (this.inMemoryCache && this.inMemoryCache.uid === uid) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[bootstrap] Using in-memory cache');
      }
      return { user: this.inMemoryCache.userProfile, config: this.inMemoryCache.appConfig };
    }

    // 2. Use local cache if valid and same user
    if (local && local.uid === uid && (now - local.timestamp < USER_PROFILE_TTL)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[bootstrap] Using local persistent cache');
      }
      this.inMemoryCache = local;
      return { user: local.userProfile, config: local.appConfig };
    }

    // 3. Otherwise, fetch from Firestore
    if (process.env.NODE_ENV === 'development') {
      console.log('[bootstrap] Fetching from Firestore');
    }
    
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data() as UserProfile;
        
        const newCache: BootstrapCache = {
          userProfile: userData,
          appConfig: null, // No global config yet
          timestamp: now,
          lastSeenUpdate: local?.lastSeenUpdate || 0,
          uid: uid
        };

        this.inMemoryCache = newCache;
        this.setLocalCache(newCache);
        
        // Throttled lastSeen update (only once every 6 hours)
        if (now - (local?.lastSeenUpdate || 0) > LAST_SEEN_TTL) {
          this.updateLastSeen(uid);
        }

        return { user: userData, config: null };
      }
    } catch (error: any) {
      const isQuotaError = error.message?.toLowerCase().includes('quota');
      const isOfflineError = error.message?.toLowerCase().includes('offline') || error.code === 'unavailable';

      if (isQuotaError || isOfflineError) {
        console.warn(`[bootstrap] ${isQuotaError ? 'Quota exceeded' : 'Offline'}. Fallback logic active.`);
      } else {
        console.error('[bootstrap] Firestore error:', error);
      }
      
      // Fallback to local cache if quota error or offline
      if ((isQuotaError || isOfflineError) && local && local.uid === uid) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[bootstrap] Using stale local cache as fallback');
        }
        return { user: local.userProfile, config: local.appConfig };
      }
      
      // If it's a quota error but no cache, return nulls but don't throw to avoid noisy initAuth logs
      if (isQuotaError) {
        return { user: null, config: null };
      }

      throw error;
    }

    return { user: null, config: null };
  }

  private async updateLastSeen(uid: string) {
    try {
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, { 
        lastSeen: new Date().toISOString(),
        lastSeenAt: serverTimestamp() 
      }, { merge: true });
      
      const local = this.getLocalCache();
      if (local) {
        local.lastSeenUpdate = Date.now();
        this.setLocalCache(local);
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('[bootstrap] lastSeen updated (throttled)');
      }
    } catch (e) {
      console.warn('[bootstrap] Failed to update lastSeen', e);
    }
  }

  clearCache() {
    this.inMemoryCache = null;
    localStorage.removeItem(BOOTSTRAP_CACHE_KEY);
  }
}

export const bootstrapService = new BootstrapService();
